/**
 * prescore.ts — Calcul local instantané de correspondance CV ↔ Offre.
 *
 * Algorithme : overlap de mots-clés entre les compétences du CV
 * et les compétences requises de l'offre (parsedData.competencesRequises).
 * Aucun appel réseau, aucun LLM — résultat disponible immédiatement.
 *
 * Retourne un score 0-100 ou null si les données sont insuffisantes.
 */

import type { ParsedJobOffer } from '../types';

/** Normalise un token en minuscule sans ponctuation */
function norm(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, ' ').trim();
}

/**
 * Extrait les mots-clés significatifs d'une liste de compétences.
 * Retourne les tokens de plus de 2 caractères, dédupliqués.
 */
function extractKeywords(items: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const item of items) {
    const tokens = norm(item).split(/\s+/).filter((t) => t.length > 2);
    for (const t of tokens) {
      if (!seen.has(t)) {
        seen.add(t);
        result.push(t);
      }
    }
  }
  return result;
}

/**
 * Calcule le prescore (0-100) entre la liste de compétences du CV
 * et les données parsées d'une offre d'emploi.
 *
 * @returns null si les données sont insuffisantes pour calculer
 */
export function computePrescore(
  cvSkills: string[],
  parsedData: ParsedJobOffer | undefined | null,
): number | null {
  if (!parsedData) return null;

  // Mots-clés de l'offre : compétences requises + titre + profil recherché
  const offerRaw: string[] = [
    ...(parsedData.competencesRequises ?? []),
    parsedData.titre ?? '',
    parsedData.profilRecherche ?? '',
  ].filter(Boolean);

  const offerKeywords = extractKeywords(offerRaw);
  if (offerKeywords.length === 0 || cvSkills.length === 0) return null;

  const cvKeywords = extractKeywords(cvSkills);

  // Compte les mots-clés de l'offre trouvés dans les compétences du CV
  let matched = 0;
  for (const kw of offerKeywords) {
    if (cvKeywords.some((c) => c.includes(kw) || kw.includes(c))) {
      matched++;
    }
  }

  return Math.round((matched / offerKeywords.length) * 100);
}
